import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { SignedInTokens, SignupConfig, WebsiteCheck, useSignupApi } from '../../../common/hooks/use-signup-api.hook';
import AuthClientStore from '../../../common/services/auth-client.service';
import { AuthLayout, AuthLeftPanel, AuthRightPanel } from '../../../components/design-system/auth';
import { SignupErrorCode, readSignupError } from '../../../modules/signup/signup-errors';
import { rememberSignupSite } from '../../../modules/signup/signup-storage';
import { pickSignupLanguage } from '../../../modules/signup/signup-language';
import { useHumanCheck } from '../../../modules/signup/use-human-check';
import { startJourney } from '../../../modules/signup/signup-journey';
import { startAnalytics, trackAccountCreated } from '../../../modules/signup/signup-analytics';
import { BookDemoDialog } from '../../../modules/signup/BookDemoDialog';
import { AccountStep } from './AccountStep';
import { WebsiteStep, looksLikeWebsite } from './WebsiteStep';

type Step = 'website' | 'account';

/**
 * "Start for free": the landing page's button lands here, signed out.
 *
 * Two short steps — the website, then the account — and the vendor is in the
 * app. The website comes first because it is the account: the domain is what
 * gets claimed, and what the next screen asks them to connect. The landing
 * page may pass it along (`/start?website=acme.com`), in which case it is
 * checked straight away.
 */
const SignupPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { search } = useLocation();
    const { me, setGlobalLoading } = useAuthContext();
    const api = useSignupApi();

    // When the form opened: the server refuses one filled in faster than a person could.
    const [startedAt] = useState(() => Date.now());
    const [config, setConfig] = useState<SignupConfig | null | undefined>(undefined);
    const human = useHumanCheck(config);
    // Who this visitor is before there is an account, and where they came from (docs/plg-review.md).
    const [journey] = useState(() => startJourney(search));
    const [demoOpen, setDemoOpen] = useState(false);

    const [step, setStep] = useState<Step>('website');
    const [website, setWebsite] = useState(() => new URLSearchParams(search).get('website') || new URLSearchParams(search).get('url') || '');
    const [site, setSite] = useState<WebsiteCheck | null>(null);
    const [checking, setChecking] = useState(false);
    const [websiteError, setWebsiteError] = useState<SignupErrorCode | null>(null);
    const [showInvalid, setShowInvalid] = useState(false);

    // Google Analytics joins the visit from ardealy.com, with the consent given there (signup-analytics).
    useEffect(() => {
        startAnalytics(search);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The visitor's language, not the app's English default (see pickSignupLanguage).
    useEffect(() => {
        const lng = pickSignupLanguage(search, typeof navigator !== 'undefined' ? navigator.languages || [navigator.language] : []);
        if (lng !== i18n?.language) i18n?.changeLanguage?.(lng);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let live = true;
        api.config()
            .then((c) => live && setConfig(c || { googleClientId: null, turnstileSiteKey: null }))
            // No config: email + password with the puzzle still works.
            .catch(() => live && setConfig(null));
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkWebsite = async () => {
        setWebsiteError(null);
        if (!looksLikeWebsite(website)) {
            setShowInvalid(true);
            return;
        }
        setShowInvalid(false);
        setChecking(true);
        try {
            const found = await api.checkWebsite(website.trim(), journey);
            if (!found?.available) {
                setWebsiteError('DOMAIN_TAKEN');
                return;
            }
            setSite(found);
            setStep('account');
        } catch (e) {
            setWebsiteError(await readSignupError(e));
        } finally {
            setChecking(false);
        }
    };

    // Arriving with a website from the landing page: check it at once.
    const autoChecked = useRef(false);
    useEffect(() => {
        if (autoChecked.current || !website) return;
        autoChecked.current = true;
        if (looksLikeWebsite(website)) checkWebsite();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Signed in exactly as the login page leaves it: both tokens stored, then the account loaded. */
    const signedIn = async (tokens: SignedInTokens, method: 'email' | 'google' = 'email') => {
        AuthClientStore.setAccessToken(tokens.access_token);
        AuthClientStore.setRefreshToken(tokens.refresh_token);
        if (site) rememberSignupSite({ domain: site.domain, platform: site.platform, title: site.title });
        setGlobalLoading(true, t('SIGNUP.LOADING'));
        try {
            await me();
        } catch (e) {
            setGlobalLoading(false);
            throw e;
        }
        await trackAccountCreated(method);
        navigate('/dashboard', { replace: true });
    };

    const leftPanel = (
        <AuthLeftPanel
            title={step === 'website' ? t('SIGNUP.WEBSITE.TITLE') : t('SIGNUP.ACCOUNT.TITLE')}
            subtitle={step === 'website' ? t('SIGNUP.WEBSITE.SUBTITLE') : t('SIGNUP.ACCOUNT.SUBTITLE')}
            fadeClassName="login-panel-fade"
            switchText={t('SIGNUP.HAVE_ACCOUNT')}
            switchLinkText={t('SIGNUP.SIGN_IN')}
            switchLinkHref="/login"
        >
            <p className="animate-form-item animate-delay-1 -mt-4 mb-6 text-center text-xs text-muted-foreground" data-testid="signup-step">
                {t('SIGNUP.STEP', { current: step === 'website' ? 1 : 2, total: 2 })}
            </p>
            {step === 'website' || !site ? (
                <WebsiteStep
                    value={website}
                    onChange={(v) => {
                        setWebsite(v);
                        setShowInvalid(false);
                        setWebsiteError(null);
                    }}
                    onSubmit={checkWebsite}
                    busy={checking}
                    error={websiteError}
                    showInvalid={showInvalid}
                    onBookDemo={() => setDemoOpen(true)}
                />
            ) : (
                <AccountStep
                    website={website.trim()}
                    site={site}
                    config={config ?? null}
                    human={human}
                    startedAt={startedAt}
                    journey={journey}
                    onChangeSite={() => setStep('website')}
                    onSignedIn={signedIn}
                />
            )}
        </AuthLeftPanel>
    );

    return (
        <div dir={i18n?.language === 'ar' ? 'rtl' : 'ltr'}>
            <AuthLayout leftPanel={leftPanel} rightPanel={<AuthRightPanel className="login-panel-fade" />} />
            <BookDemoDialog
                open={demoOpen}
                onOpenChange={setDemoOpen}
                config={config}
                journey={journey}
                prefill={{ website: website.trim() || undefined }}
                defaultReason="offline_store"
            />
        </div>
    );
};

export default SignupPage;
